<template>
  <div class="capture-root">
    <div class="topbar">
      <img src="@/assets/logogruas.png" alt="HomePage" class="logo-button" @click="goBack" />
      <button class="refresh-btn" @click="fetchSubmissions(1)" :disabled="loading">
        {{ loading ? 'Actualizando...' : 'Actualizar' }}
      </button>
    </div>

    <header class="header">
      <div>
        <h1>Bitacora de unidades</h1>
        <p class="header-sub">Checklists enviados por los operadores desde la app movil</p>
      </div>
      <div class="header-actions">
        <label class="filter-field">
          Unidad
          <input type="text" v-model.trim="filters.search" placeholder="Ej. G-12" />
        </label>
        <label class="filter-field">
          Turno
          <select v-model="filters.shift">
            <option value="">Todos</option>
            <option value="matutino">Matutino</option>
            <option value="vespertino">Vespertino</option>
            <option value="nocturno">Nocturno</option>
          </select>
        </label>
        <label class="filter-field">
          Desde
          <input type="date" v-model="filters.dateFrom" />
        </label>
        <label class="filter-field">
          Hasta
          <input type="date" v-model="filters.dateTo" />
        </label>
        <button class="small-btn" @click="clearFilters">Limpiar filtros</button>
      </div>
    </header>

    <section v-if="error" class="error-box">{{ error }}</section>

    <section v-if="rows.length" class="table-wrap">
      <table class="logbook-table">
        <thead>
          <tr>
            <th>Fecha y hora</th>
            <th>Unidad</th>
            <th>Categoria</th>
            <th>Operador</th>
            <th>Turno</th>
            <th>Checklist</th>
            <th>Estado</th>
            <th>Km</th>
            <th>Combustible</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.id"
            class="logbook-row"
            @click="openDetail(row)"
          >
            <td>{{ formatDateTime(row.created_at) }}</td>
            <td class="cell-strong">{{ row.unit_short_id }}</td>
            <td>{{ row.unit_category }}</td>
            <td>{{ row.employee_name }}</td>
            <td>{{ row.shift_label }}</td>
            <td>{{ row.template_name }}</td>
            <td>
              <span v-if="row.bad_count" class="status-pill status-pill--bad">
                {{ row.bad_count }} {{ row.bad_count === 1 ? 'falla' : 'fallas' }}
              </span>
              <span v-else class="status-pill status-pill--ok">Todo OK</span>
              <span v-if="row.has_observations" class="status-pill status-pill--obs" title="Tiene observaciones">Obs.</span>
            </td>
            <td>{{ row.kilometraje ?? '—' }}</td>
            <td>{{ row.combustible || '—' }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section v-else-if="!loading" class="empty-box">
      No hay checklists registrados con los filtros seleccionados.
    </section>

    <section v-if="totalRecords" class="pagination">
      <button class="small-btn" :disabled="page <= 1 || loading" @click="fetchSubmissions(page - 1)">
        Anterior
      </button>
      <span class="pagination__info">
        {{ pageRangeLabel }} de {{ totalRecords }} checklists
      </span>
      <button class="small-btn" :disabled="!hasMore || loading" @click="fetchSubmissions(page + 1)">
        Siguiente
      </button>
    </section>

    <transition name="logbook-pop">
      <div v-if="showDetailModal" class="modal-overlay" @click.self="closeDetail">
        <div class="modal-card logbook-card">
          <div class="modal-head">
            <div>
              <h3>{{ detail.data?.unit_short_id || detail.summary?.unit_short_id }} · {{ detail.data?.template_name || detail.summary?.template_name }}</h3>
              <p class="modal-subtitle">
                {{ formatDateTime(detail.data?.created_at || detail.summary?.created_at) }}
              </p>
            </div>
            <button class="small-btn" @click="closeDetail">Cerrar</button>
          </div>

          <div v-if="detail.loading" class="detail-state">Cargando checklist...</div>
          <div v-else-if="detail.error" class="detail-state detail-state--error">{{ detail.error }}</div>
          <div v-else-if="detail.data" class="detail-body">
            <div class="detail-meta">
              <div class="meta-card">
                <span>Operador</span>
                <strong>{{ detail.data.employee_name }}</strong>
              </div>
              <div class="meta-card">
                <span>Turno</span>
                <strong>{{ detail.data.shift_label }}</strong>
              </div>
              <div class="meta-card">
                <span>Kilometraje</span>
                <strong>{{ detail.data.kilometraje ?? '—' }}</strong>
              </div>
              <div class="meta-card">
                <span>Combustible</span>
                <strong>{{ detail.data.combustible || '—' }}</strong>
              </div>
              <div class="meta-card" :class="detail.data.bad_count ? 'meta-card--bad' : 'meta-card--ok'">
                <span>Resultado</span>
                <strong>
                  {{ detail.data.bad_count ? `${detail.data.bad_count} de ${detail.data.total_items} con falla` : 'Todo OK' }}
                </strong>
              </div>
            </div>

            <div v-if="detail.data.observations" class="observations-box">
              <h4>Observaciones</h4>
              <p>{{ detail.data.observations }}</p>
            </div>

            <label v-if="detail.data.bad_count" class="only-issues-toggle">
              <input type="checkbox" v-model="onlyIssues" />
              Mostrar solo fallas
            </label>

            <div v-for="section in visibleSections" :key="section.key" class="checklist-section">
              <h4>{{ section.label }}</h4>
              <div class="checklist-items">
                <div
                  v-for="item in section.items"
                  :key="item.key"
                  class="checklist-item"
                  :class="{ 'checklist-item--bad': item.status === 'bad' }"
                >
                  <span class="checklist-item__label">{{ item.label }}</span>
                  <span
                    class="status-pill"
                    :class="{
                      'status-pill--ok': item.status === 'ok',
                      'status-pill--bad': item.status === 'bad',
                      'status-pill--empty': !item.status,
                    }"
                  >
                    {{ item.status === 'ok' ? 'OK' : item.status === 'bad' ? 'Falla' : 'Sin dato' }}
                  </span>
                  <p v-if="item.comment" class="checklist-item__comment">{{ item.comment }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'UnitLogbook',
  data() {
    return {
      permissions: [],
      rows: [],
      loading: false,
      error: '',
      page: 1,
      pageSize: 25,
      totalRecords: 0,
      hasMore: false,
      filters: {
        search: '',
        shift: '',
        dateFrom: '',
        dateTo: '',
      },
      searchDebounceId: null,
      showDetailModal: false,
      onlyIssues: false,
      detail: {
        loading: false,
        error: '',
        summary: null,
        data: null,
      },
    }
  },
  computed: {
    pageRangeLabel() {
      if (!this.totalRecords) return '0'
      const start = (this.page - 1) * this.pageSize + 1
      const end = Math.min(this.page * this.pageSize, this.totalRecords)
      return `${start}-${end}`
    },
    visibleSections() {
      const sections = this.detail.data?.sections || []
      if (!this.onlyIssues) return sections
      return sections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => item.status === 'bad'),
        }))
        .filter((section) => section.items.length)
    },
  },
  watch: {
    'filters.search'() {
      clearTimeout(this.searchDebounceId)
      this.searchDebounceId = setTimeout(() => this.fetchSubmissions(1), 350)
    },
    'filters.shift'() {
      this.fetchSubmissions(1)
    },
    'filters.dateFrom'() {
      this.fetchSubmissions(1)
    },
    'filters.dateTo'() {
      this.fetchSubmissions(1)
    },
  },
  created() {
    this.fetchCurrentUser()
  },
  beforeUnmount() {
    clearTimeout(this.searchDebounceId)
  },
  methods: {
    apiBase() {
      return process.env.API_DIRECTORY || ''
    },
    authHeaders() {
      return { Authorization: `Token ${localStorage.getItem('authToken')}` }
    },
    hasPerm(perm) {
      if (!perm) return false
      if (this.permissions.includes(perm)) return true
      return this.permissions.includes(`access_control.${perm}`)
    },
    async fetchCurrentUser() {
      try {
        const { data } = await axios.get(`${this.apiBase()}/current-user/`, {
          headers: this.authHeaders(),
        })
        this.permissions = data.permissions || []
        if (!this.hasPerm('ui_access_maintenance_module')) {
          this.$router.push({ name: 'MainPanel' })
          return
        }
        this.fetchSubmissions(1)
      } catch (error) {
        this.$router.push({ name: 'MainPanel' })
      }
    },
    async fetchSubmissions(page) {
      this.loading = true
      this.error = ''
      try {
        const params = { page, page_size: this.pageSize }
        if (this.filters.search) params.search = this.filters.search
        if (this.filters.shift) params.shift = this.filters.shift
        if (this.filters.dateFrom) params.date_from = this.filters.dateFrom
        if (this.filters.dateTo) params.date_to = this.filters.dateTo
        const { data } = await axios.get(`${this.apiBase()}/maintenance/unit-checklists`, {
          params,
          headers: this.authHeaders(),
        })
        this.rows = Array.isArray(data?.results) ? data.results : []
        this.page = Number(data?.page) || page
        this.totalRecords = Number(data?.total_records) || 0
        this.hasMore = Boolean(data?.has_more)
      } catch (error) {
        this.error = 'No se pudo cargar la bitacora de unidades.'
      } finally {
        this.loading = false
      }
    },
    clearFilters() {
      clearTimeout(this.searchDebounceId)
      const wasEmpty = !this.filters.search && !this.filters.shift && !this.filters.dateFrom && !this.filters.dateTo
      this.filters = { search: '', shift: '', dateFrom: '', dateTo: '' }
      // The watchers only fire when something actually changed.
      if (wasEmpty) this.fetchSubmissions(1)
    },
    async openDetail(row) {
      this.onlyIssues = false
      this.detail = { loading: true, error: '', summary: row, data: null }
      this.showDetailModal = true
      try {
        const { data } = await axios.get(`${this.apiBase()}/maintenance/unit-checklists/${row.id}`, {
          headers: this.authHeaders(),
        })
        this.detail = { ...this.detail, loading: false, data }
      } catch (error) {
        this.detail = { ...this.detail, loading: false, error: 'No se pudo cargar el checklist.' }
      }
    },
    closeDetail() {
      this.showDetailModal = false
    },
    formatDateTime(isoStr) {
      if (!isoStr) return ''
      const parsed = new Date(isoStr)
      if (Number.isNaN(parsed.getTime())) return isoStr
      return parsed.toLocaleString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    },
    goBack() {
      this.$router.push({ name: 'MaintenanceModule' })
    },
  },
}
</script>

<style scoped>
.capture-root {
  min-height: 100vh;
  background:
    radial-gradient(circle at 5% 0%, rgba(34, 197, 94, 0.14), transparent 35%),
    radial-gradient(circle at 92% 2%, rgba(124, 58, 237, 0.14), transparent 32%),
    #eef3fb;
  padding: 76px 16px 24px;
}
.topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 64px;
  background: #ffc434;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  z-index: 20;
}
.logo-button {
  height: 65%;
  object-fit: contain;
  cursor: pointer;
}
.refresh-btn {
  border: none;
  background: #111827;
  color: #fff;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
}
.header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 10px;
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(148, 163, 184, 0.26);
  border-radius: 14px;
  padding: 10px 14px;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.07);
}
.header h1 {
  margin: 0;
  color: #0f172a;
  font-size: 1.1rem;
  line-height: 1.1;
}
.header-sub {
  margin: 3px 0 0;
  color: #64748b;
  font-size: 0.82rem;
}
.header-actions {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}
.filter-field {
  display: grid;
  gap: 4px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
}
.filter-field input,
.filter-field select {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px;
  background: #fff;
}
.table-wrap {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #dbe6f7;
  border-radius: 12px;
  overflow: auto;
  box-shadow: 0 20px 36px rgba(15, 23, 42, 0.09);
}
.logbook-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
.logbook-table th,
.logbook-table td {
  border-bottom: 1px solid #edf2fb;
  padding: 9px 10px;
  text-align: left;
  font-size: 0.84rem;
  white-space: nowrap;
}
.logbook-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  color: #0f172a;
  border-bottom: 1px solid #dbe6f7;
  font-weight: 700;
  font-size: 0.8rem;
}
.logbook-row {
  cursor: pointer;
  transition: background 0.12s ease;
}
.logbook-row:hover {
  background: #eff6ff;
}
.cell-strong {
  font-weight: 700;
  color: #0f172a;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 0.72rem;
  font-weight: 700;
}
.status-pill + .status-pill {
  margin-left: 5px;
}
.status-pill--ok {
  background: #dcfce7;
  color: #166534;
}
.status-pill--bad {
  background: #fee2e2;
  color: #991b1b;
}
.status-pill--obs {
  background: #dbeafe;
  color: #1e40af;
}
.status-pill--empty {
  background: #f1f5f9;
  color: #64748b;
}
.pagination {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.pagination__info {
  font-size: 0.82rem;
  font-weight: 600;
  color: #475569;
}
.empty-box,
.error-box {
  margin-top: 12px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 12px;
}
.error-box {
  color: #b91c1c;
  border-color: #fecaca;
}
.small-btn {
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
  font-weight: 600;
}
.small-btn:disabled {
  opacity: 0.55;
  cursor: default;
}
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  backdrop-filter: blur(2px);
}
.modal-card {
  width: min(820px, 100%);
  max-height: 86vh;
  background: linear-gradient(180deg, #ffffff, #f8fbff);
  border-radius: 14px;
  border: 1px solid #d7e3f7;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 46px rgba(2, 6, 23, 0.25);
}
.logbook-card {
  width: min(880px, 100%);
}
.modal-head {
  padding: 12px;
  border-bottom: 1px solid #dbe7f7;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  background: linear-gradient(90deg, #eff6ff, #ecfeff);
}
.modal-head h3 {
  margin: 0;
  color: #0f172a;
  font-size: 1rem;
}
.modal-subtitle {
  margin: 2px 0 0;
  font-size: 0.82rem;
  color: #475569;
}
.detail-state {
  padding: 28px 16px;
  text-align: center;
  color: #475569;
  font-weight: 600;
}
.detail-state--error {
  color: #b91c1c;
}
.detail-body {
  overflow-y: auto;
  padding: 14px;
  display: grid;
  gap: 14px;
  align-content: start;
}
.detail-meta {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
}
.meta-card {
  border: 1px solid #dbe5f3;
  border-radius: 10px;
  background: #fff;
  padding: 9px 11px;
  display: grid;
  gap: 3px;
}
.meta-card span {
  font-size: 0.68rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-weight: 700;
}
.meta-card strong {
  font-size: 0.92rem;
  color: #0f172a;
}
.meta-card--ok {
  border-color: #86efac;
  background: #f0fdf4;
}
.meta-card--ok strong {
  color: #166534;
}
.meta-card--bad {
  border-color: #fca5a5;
  background: #fef2f2;
}
.meta-card--bad strong {
  color: #991b1b;
}
.observations-box {
  border: 1px solid #fde68a;
  background: #fffbeb;
  border-radius: 10px;
  padding: 10px 12px;
}
.observations-box h4 {
  margin: 0 0 4px;
  font-size: 0.8rem;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.observations-box p {
  margin: 0;
  color: #78350f;
  font-size: 0.86rem;
  white-space: pre-wrap;
}
.only-issues-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.82rem;
  font-weight: 700;
  color: #991b1b;
  cursor: pointer;
}
.checklist-section h4 {
  margin: 0 0 7px;
  font-size: 0.86rem;
  color: #1e3a8a;
  border-bottom: 1px solid #dbe7f7;
  padding-bottom: 5px;
}
.checklist-items {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 7px;
}
.checklist-item {
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  background: #fff;
  padding: 7px 9px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}
.checklist-item--bad {
  border-color: #fca5a5;
  background: #fef2f2;
}
.checklist-item__label {
  font-size: 0.8rem;
  color: #1f2937;
  font-weight: 600;
}
.checklist-item__comment {
  margin: 0;
  width: 100%;
  font-size: 0.76rem;
  color: #7f1d1d;
  font-style: italic;
}
.logbook-pop-enter-active {
  transition: opacity 0.25s ease;
}
.logbook-pop-leave-active {
  transition: opacity 0.2s ease;
}
.logbook-pop-enter-active .modal-card {
  transition: transform 0.32s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.28s ease;
}
.logbook-pop-leave-active .modal-card {
  transition: transform 0.18s ease, opacity 0.18s ease;
}
.logbook-pop-enter-from,
.logbook-pop-leave-to {
  opacity: 0;
}
.logbook-pop-enter-from .modal-card {
  transform: translateY(22px) scale(0.95);
  opacity: 0;
}
.logbook-pop-leave-to .modal-card {
  transform: translateY(12px) scale(0.97);
  opacity: 0;
}
@media (max-width: 980px) {
  .header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
